import type { ContentObjectView, EventContentObjectList } from 'models';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { canDeleteContentObjectProfile, usePermissions } from 'permissions';
import { Icon, type MenuProps, message } from 'ui';
import { useUser } from 'features/auth';
import { useUnbookmarkObjectMutation } from 'features/bookmark';
import { useReportContentObjectModalConfirm } from 'features/report';
import { useDeleteEventConfirmModal } from './useDeleteEventConfirmModal';

type P = {
  item: EventContentObjectList;
  view: ContentObjectView;
};

type UseCreateActionsReturn = {
  actions: MenuProps['items'];
  contexts?: ReactNode[];
};

export const useCreateActions = ({ item, view }: P): UseCreateActionsReturn => {
  const { t } = useTranslation();
  const { confirm: confirmDeleteEvent, contextHolder: deleteContentObjectContextHolder } = useDeleteEventConfirmModal();

  const { confirm: confirmReportEvent, contextHolder: reportContentObjectContextHolder } =
    useReportContentObjectModalConfirm();

  const { canReportContentObject } = usePermissions();
  const { railsUserData: viewer } = useUser();
  const { mutate: unbookmark } = useUnbookmarkObjectMutation();

  return useMemo(() => {
    if (view === 'bookmark') {
      return {
        actions: [
          {
            icon: <Icon icon="bookmark-line" size="small" />,
            key: 'unbookmark',
            label: t('Unbookmark'),
            onClick() {
              unbookmark(
                { contentObject: item, type: 'rails' },
                {
                  onSuccess() {
                    message.success('Successfully unbookmarked');
                  },
                },
              );
            },
          },
        ],
      };
    }

    const actions = [];

    if (canReportContentObject({ contentObject: item })) {
      actions.push({
        icon: <Icon icon="flag-1-line" size="small" />,
        key: 'report',
        label: t('Report'),
        onClick() {
          confirmReportEvent({
            id: item.id,
            onSuccess() {
              message.success('Successfully reported');
            },
            title: 'Are you sure you want to report this event?',
          });
        },
      });
    }

    if (canDeleteContentObjectProfile({ contentObject: item, viewer })) {
      actions.push({
        danger: true,
        icon: <Icon color="error" icon="delete-2-line" size="small" />,
        key: 'delete',
        label: t('Delete'),
        onClick() {
          confirmDeleteEvent({
            item,
            onSuccess() {
              message.success('Successfully deleted');
            },
            title: 'Are you sure you want to delete this event?',
          });
        },
      });
    }

    return {
      actions,
      contexts: [deleteContentObjectContextHolder, reportContentObjectContextHolder],
    };
  }, [
    view,
    canReportContentObject,
    item,
    viewer,
    deleteContentObjectContextHolder,
    reportContentObjectContextHolder,
    t,
    confirmReportEvent,
    confirmDeleteEvent,
    unbookmark,
  ]);
};
